'use client';

// Whether the page runs inside the Auctogon Admin phone app (BL-2342), read
// through the native-device gateway (ADR 064 — Native Shell § 5). Outside
// production a browser may emulate the app with `?emulate-native-app=1`; the
// gateway resolver alone decides that (BL-2450), so this answer and the device
// store always agree. Production can never turn it on.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { getNativeDeviceGateway, mayBeNativeApp } from '../gateways/native-device';

/** Nothing to subscribe to: the bridge global is fixed for the page's life. */
const subscribeNever = () => () => {};

/** What {@link useIsNativeApp} returns. */
export interface NativeAppDetection {
  /** True inside the native admin app, or a non-production emulation of it. */
  isNativeApp: boolean;
  /**
   * False until the platform is known (the server render, the first paint, and
   * the gateway's async load). A surface whose browser branch would flash inside
   * the app waits for this.
   */
  detected: boolean;
}

/**
 * Whether the page runs inside the native admin app (or, outside production,
 * a browser emulating it).
 *
 * @returns the answer, and whether it is settled yet.
 */
export function useIsNativeApp(): NativeAppDetection {
  const [platform, setPlatform] = useState<{ native: boolean; detected: boolean }>({
    native: false,
    detected: false,
  });
  // A page with no native bridge is a browser: answered synchronously, without
  // fetching the Capacitor adapter chunk (desktop never needs it). The server
  // snapshot is "unknown", so hydration matches.
  const browserOnly = useSyncExternalStore(subscribeNever, () => !mayBeNativeApp(), () => false);

  useEffect(() => {
    if (browserOnly) return;
    let cancelled = false;
    getNativeDeviceGateway()
      .then((gateway) => {
        if (!cancelled) setPlatform({ native: gateway.isNativeApp(), detected: true });
      })
      .catch((err: unknown) => {
        // The adapter chunk failed to load. Treat the page as a browser (the
        // desktop behaviour) and say so; the next mount retries.
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). The page falls back to the desktop behaviour.
        console.error('[useIsNativeApp] native-device gateway failed to load; treating as a browser', err);
        if (!cancelled) setPlatform({ native: false, detected: true });
      });
    return () => {
      cancelled = true;
    };
  }, [browserOnly]);

  return {
    isNativeApp: platform.native,
    detected: browserOnly || platform.detected,
  };
}
